import React, { useEffect, useState } from "react";
import { BarChart3 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function HoursByProjectReport() {
    const [start, setStart] = useState("");
    const [end, setEnd] = useState("");
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (start) params.set("start", start);
            if (end) params.set("end", end);
            const r = await api.get(`/reports/hours-by-project${params.toString() ? `?${params}` : ""}`);
            setData(r.data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to load report");
        } finally {
            setLoading(false);
        }
    };

    // Loads on mount (all-time, since start/end start empty) and whenever the
    // range is cleared back to all-time; date-field edits are applied via the
    // explicit Apply button below rather than firing a request per keystroke.
    useEffect(() => { if (!start && !end) load(); }, [start, end]); // eslint-disable-line react-hooks/exhaustive-deps

    const clearRange = () => { setStart(""); setEnd(""); };

    return (
        <div className="paper-bg pb-28" data-testid="hours-by-project-report">
            <div className="max-w-2xl mx-auto px-4 py-6 space-y-5">
                <h2 className="font-bold text-2xl flex items-center gap-2">
                    <BarChart3 className="h-5 w-5 text-brand-navy" /> Hours by Project
                </h2>
                <p className="text-sm text-brand-charcoal/70">
                    Total worked hours recorded against each project. Defaults to all-time — narrow to a date range if you need it.
                </p>

                <div className="bg-white rounded-2xl border border-brand-charcoal/15 shadow-card p-5 flex flex-wrap items-end gap-3">
                    <div>
                        <label className="block text-[10px] uppercase tracking-widest text-brand-navy mb-1">Start date</label>
                        <input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="h-11 border border-brand-charcoal/20 rounded-xl px-3 text-brand-charcoal" data-testid="report-start-date" />
                    </div>
                    <div>
                        <label className="block text-[10px] uppercase tracking-widest text-brand-navy mb-1">End date</label>
                        <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="h-11 border border-brand-charcoal/20 rounded-xl px-3 text-brand-charcoal" data-testid="report-end-date" />
                    </div>
                    <button onClick={load} className="h-11 px-5 rounded-xl bg-brand-navy text-brand-bone font-bold" data-testid="run-report-button">Apply</button>
                    {(start || end) && (
                        <button onClick={clearRange} className="h-11 px-4 rounded-xl bg-brand-stone text-brand-charcoal font-bold" data-testid="clear-range-button">All-time</button>
                    )}
                </div>

                {loading && !data && <div className="text-brand-charcoal/60 text-center py-8">Loading…</div>}

                {data && (
                    <div className="bg-white rounded-2xl border border-brand-charcoal/15 shadow-card overflow-hidden">
                        {data.rows.length === 0 ? (
                            <div className="p-10 text-center text-brand-charcoal/50" data-testid="no-project-hours">
                                No worked hours recorded {start || end ? "in this date range" : "yet"}.
                            </div>
                        ) : (
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="bg-brand-stone text-left text-[10px] uppercase tracking-widest text-brand-charcoal/70">
                                        <th className="px-4 py-3">Project</th>
                                        <th className="px-4 py-3">Job Number</th>
                                        <th className="px-4 py-3 text-right">Hours</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.rows.map((r) => (
                                        <tr key={r.project_id} className="border-t border-brand-charcoal/10" data-testid={`hours-row-${r.project_id}`}>
                                            <td className="px-4 py-3 font-semibold text-brand-charcoal">{r.name}</td>
                                            <td className="px-4 py-3 text-brand-charcoal/60">{r.job_number}</td>
                                            <td className="px-4 py-3 text-right font-bold text-brand-navy">{r.hours.toFixed(2)}h</td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot>
                                    <tr className="border-t-2 border-brand-charcoal/20 bg-brand-stone/60 font-bold">
                                        <td className="px-4 py-3" colSpan={2}>Grand total</td>
                                        <td className="px-4 py-3 text-right text-brand-navy" data-testid="grand-total-hours">{data.grand_total.toFixed(2)}h</td>
                                    </tr>
                                </tfoot>
                            </table>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
